/* VALEN VARET · Disponibilidad, expedición estimada y aviso de rebajas en la ficha de producto.
   - Solo lee datos que el tema ya pinta en la página (sin llamadas a la red ni credenciales).
   - La variante se lee del formulario de compra de la MISMA sección, nunca del primer botón de la página. */
(() => {
  if (window.vvPdpStatus) return;

  const watchers = new Map(); // section -> { els:Set, observer, raf }

  function sectionOf(el) {
    return el.closest('.shopify-section') || document.body;
  }

  function currentVariantId(el) {
    const sec = sectionOf(el);
    const input = sec.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (input && input.value) return String(input.value);
    const fromUrl = new URLSearchParams(location.search).get('variant');
    if (fromUrl) return fromUrl;
    const def = el.querySelector('[data-variant]');
    return def ? def.getAttribute('data-variant') : null;
  }

  function notify(sec) {
    const w = watchers.get(sec);
    if (!w || w.raf) return;
    w.raf = requestAnimationFrame(() => {
      w.raf = 0;
      w.els.forEach((el) => el.isConnected && el.update());
    });
  }

  function register(el) {
    const sec = sectionOf(el);
    let w = watchers.get(sec);
    if (!w) {
      w = { els: new Set(), raf: 0, observer: null };
      w.observer = new MutationObserver(() => notify(sec));
      w.observer.observe(sec, { subtree: true, childList: true, attributes: true, attributeFilter: ['value'] });
      sec.addEventListener('change', () => notify(sec));
      watchers.set(sec, w);
    }
    w.els.add(el);
  }

  function unregister(el) {
    watchers.forEach((w, sec) => {
      if (!w.els.delete(el) || w.els.size) return;
      w.observer.disconnect();
      watchers.delete(sec);
    });
  }

  document.addEventListener('variant:update', () => watchers.forEach((_, sec) => notify(sec)));
  window.addEventListener('popstate', () => watchers.forEach((_, sec) => notify(sec)));

  /* ------------------------------------------------ Disponibilidad */
  class VVStockStatus extends HTMLElement {
    connectedCallback() {
      try {
        this.data = JSON.parse(this.querySelector('[data-vv-stock-data]').textContent);
      } catch (e) {
        this.data = null;
      }
      this.line = this.querySelector('[data-vv-stock-line]');
      this.textEl = this.querySelector('[data-vv-stock-text]');
      this.threshold = parseInt(this.dataset.threshold, 10) || 0;
      this.lastKey = '';
      register(this);
      this.update();
    }
    disconnectedCallback() { unregister(this); }

    update() {
      if (!this.data || !this.line) return this.hide();
      const id = currentVariantId(this);
      const v = id && this.data.variants[id];
      if (!v) return this.hide(); // variante desconocida o error: mejor ocultar que mostrar un dato dudoso
      const t = this.data.texts;
      let state = 'neutral';
      let text = '';
      let qty = null;
      if (v.t) {
        const q = Number(v.q);
        if (q > 0 && q <= this.threshold) {
          state = 'low';
          qty = q;
          text = q === 1 ? t.low_one : t.low_many;
        } else if (q > this.threshold) {
          text = t.in_stock;
        } else if (v.p === 'continue' && v.a) {
          text = t.backorder;
        } else {
          state = 'soldout';
          text = t.sold_out;
        }
      } else if (v.a) {
        text = t.untracked; // sin control de inventario: nunca una cifra
      } else {
        state = 'soldout';
        text = t.sold_out;
      }
      if (!text) return this.hide();
      const key = id + '|' + state + '|' + qty;
      if (key === this.lastKey) return;
      this.lastKey = key;
      this.textEl.textContent = '';
      if (qty !== null && text.includes('[n]')) {
        const [before, after] = text.split('[n]');
        const b = document.createElement('b');
        b.textContent = String(qty);
        this.textEl.append(before, b, after);
      } else {
        this.textEl.append(text);
      }
      if (state === 'low' && t.campaign) this.textEl.append(' ' + t.campaign);
      this.line.dataset.state = state;
      this.line.dataset.variant = id;
      this.line.hidden = false;
    }

    hide() {
      this.lastKey = '';
      if (this.line) this.line.hidden = true;
    }
  }

  /* ------------------------------------------ Expedición estimada */
  const DAYS = ['dom.', 'lun.', 'mar.', 'miér.', 'jue.', 'vie.', 'sáb.'];
  const MONTHS = ['ene.', 'feb.', 'mar.', 'abr.', 'may.', 'jun.', 'jul.', 'ago.', 'sept.', 'oct.', 'nov.', 'dic.'];

  function madridParts(epochS) {
    const f = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23',
    });
    const p = {};
    f.formatToParts(new Date(epochS * 1000)).forEach((x) => { p[x.type] = x.value; });
    return { y: +p.year, m: +p.month, d: +p.day, h: +p.hour };
  }

  class VVShipInfo extends HTMLElement {
    connectedCallback() {
      this.out = this.querySelector('[data-vv-ship-range]');
      if (!this.out) return;
      const server = parseInt(this.dataset.now, 10);
      const client = Math.floor(Date.now() / 1000);
      let now = client;
      if (!server || Math.abs(client - server) > 36 * 3600) now = server || client; // reloj del dispositivo poco fiable
      const min = Math.max(0, parseInt(this.dataset.min, 10) || 0);
      const max = Math.max(min, parseInt(this.dataset.max, 10) || 0);
      const cutoff = parseInt(this.dataset.cutoff, 10) || 0;
      const holidays = new Set((this.dataset.holidays || '').split(/[\s,;]+/).filter(Boolean));
      const mp = madridParts(now);
      const start = new Date(Date.UTC(mp.y, mp.m - 1, mp.d));
      if (cutoff > 0 && mp.h >= cutoff) start.setUTCDate(start.getUTCDate() + 1);
      const iso = (d) => d.toISOString().slice(0, 10);
      const working = (d) => { const w = d.getUTCDay(); return w !== 0 && w !== 6 && !holidays.has(iso(d)); };
      const addWorking = (n) => {
        const d = new Date(start);
        if (n === 0) { while (!working(d)) d.setUTCDate(d.getUTCDate() + 1); return d; }
        let c = 0;
        while (c < n) { d.setUTCDate(d.getUTCDate() + 1); if (working(d)) c++; }
        return d;
      };
      const fmt = (d) => DAYS[d.getUTCDay()] + ' ' + d.getUTCDate() + ' ' + MONTHS[d.getUTCMonth()];
      const a = addWorking(min);
      const b = addWorking(max);
      this.out.textContent = iso(a) === iso(b) ? 'el ' + fmt(a) : 'entre el ' + fmt(a) + ' y el ' + fmt(b);
    }
  }

  /* ---------------------------------------------- Aviso de rebajas */
  class VVSaleNotice extends HTMLElement {
    connectedCallback() {
      this.sale = new Set((this.dataset.saleVariants || '').split(',').filter(Boolean));
      register(this);
      this.update();
    }
    disconnectedCallback() { unregister(this); }
    update() {
      const id = currentVariantId(this);
      this.hidden = !(id && this.sale.has(id));
    }
  }

  if (!customElements.get('vv-stock-status')) customElements.define('vv-stock-status', VVStockStatus);
  if (!customElements.get('vv-ship-info')) customElements.define('vv-ship-info', VVShipInfo);
  if (!customElements.get('vv-sale-notice')) customElements.define('vv-sale-notice', VVSaleNotice);
  window.vvPdpStatus = true;
})();
